import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import Modal from 'molecules/Modal';
import { ModalContent, ModalSubtitleText } from 'molecules/Modal/Modal.styles';
import { StyledLoadingButton } from 'molecules/InvoicePreview/InvoicePreview.styles';
import loaderGif from 'assets/images/loaderGif.gif';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PdfDocumentPreviewModalProps {
  fileName: string;
  onClose: () => void;
  pdfSource: string | null;
  title: string;
}

const PdfDocumentPreviewModal: FC<PdfDocumentPreviewModalProps> = ({
  fileName,
  onClose,
  pdfSource,
  title,
}) => {
  const [pages, setPages] = useState<number>(0);

  useEffect(() => {
    setPages(0);
  }, [pdfSource]);

  const subtitle = useMemo(
    () => (
      <Stack gap="16px" height="58px">
        <ModalSubtitleText>
          {pages ? `${pages} ${pages > 1 ? 'pages' : 'page'}` : ''}
        </ModalSubtitleText>
        {pdfSource && (
          <StyledLoadingButton
            onClick={() => {
              const link = document.createElement('a');
              link.href = pdfSource;
              link.setAttribute('download', fileName);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download PDF
          </StyledLoadingButton>
        )}
      </Stack>
    ),
    [fileName, pages, pdfSource]
  );

  return (
    <Modal
      open
      maxWidth="lg"
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      sx={{
        zIndex: 33333,
        '.MuiPaper-root': {
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          width: '1000px !important',
          '& > div:first-of-type': {
            borderRadius: '4px',
          },
        },
        '.modal-title-block': {
          background: '#FFFFFF',
        },
      }}
    >
      <ModalContent
        sx={{
          p: '0px !important',
          minHeight: '80px',
          overflowX: 'hidden',
          '.react-pdf__Page__canvas': {
            width: '1000px !important',
          },
          '.react-pdf__Page.pdf-page': {
            mb: '10px',
          },
        }}
      >
        {pdfSource ? (
          // eslint-disable-next-line
          // @ts-ignore
          <Document
            file={pdfSource}
            onLoadSuccess={({ numPages }: { numPages: number }) => {
              setPages(numPages);
            }}
            loading={<Box sx={{ minHeight: '80px' }} />}
          >
            {pages ? (
              Array.from({ length: pages }, (_, index) => (
                // eslint-disable-next-line
                // @ts-ignore
                <Page
                  key={index + 1}
                  pageNumber={index + 1}
                  className="pdf-page"
                  width={1000}
                  scale={1}
                />
              ))
            ) : (
              <Box sx={{ minHeight: '80px' }} />
            )}
          </Document>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              padding: '40px 0',
              background: '#FFFFFF !important',
            }}
          >
            <img src={loaderGif} alt="loader" />
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PdfDocumentPreviewModal;
